import { OpenedFromCreateDialogProvider } from 'components/create-dialog/CreateDialogContext';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { AlertDialog, AlertDialogContent } from 'components/ui/alert-dialog';
import { Dialog, DialogContent } from 'components/ui/dialog';
import React, {
    CSSProperties,
    Dispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { cn } from 'utils/cn';

type Props = {
    children: string | JSX.Element[] | JSX.Element;
};

type ModalOptions = {
    content: JSX.Element | undefined;
    onCancel: () => void;
    modalStyle?: CSSProperties;
    /**
     * Scopes the modal to this element instead of the viewport: portalled into it, the backdrop and
     * the card sized and centred within it, and the page around it left usable. The element has to
     * be positioned, so it is the containing block of both.
     */
    container?: RefObject<HTMLElement | null>;
    /**
     * Opened from the create dialog while it was open, or from a modal that was. The dialog's own context does
     * not reach a modal, which renders here, above the layout, so the stack hands it on to the modal's content.
     */
    fromCreateDialog?: boolean;
    /** Classes on the card and on its backdrop, for a modal its design places or dims otherwise. */
    classNames?: ModalClassNames;
    /** A press on the backdrop leaves the modal open: only its own close and cancel buttons, or Escape, close it. */
    keepOnOutsidePress?: boolean;
};

export type ModalClassNames = { panel?: string; overlay?: string };

export type ModalBehaviour = Pick<ModalOptions, 'keepOnOutsidePress'>;

type Context = {
    setModalStack: Dispatch<SetStateAction<ModalOptions[]>>;
};

export const ModalContext = React.createContext({} as Context);

/** How many modals are open. Its own context, so opening one does not re-render every `useModal` caller. */
export const ModalCountContext = React.createContext(0);

/**
 * True for the content of a modal of the stack. `ModalHeader` then draws its title as the dialog's own, which
 * names the dialog; outside the stack, where it is also rendered, there is no dialog for a title to belong to.
 */
export const InStackedModalContext = React.createContext(false);

/** True for the content of a modal the stack draws as the kit alert dialog (see {@link asksForAlertDialog}). */
export const InAlertDialogContext = React.createContext(false);

/**
 * A content element given an `alert` prop asks to be a confirmation: in the new design the stack draws it as the kit
 * alert dialog, which has no close button and which a press outside does not dismiss. Classic stacks it as any other
 * modal.
 */
const asksForAlertDialog = (content: JSX.Element) => !!(content.props as { alert?: unknown }).alert;

/** Adds a way back one step, drawn in the element `drawnIn` holds; answers how to take it off again. */
type RegisterBack = (drawnIn: RefObject<HTMLElement | null>, back: () => void) => () => void;

const ModalBackContext = React.createContext<RegisterBack | null>(null);

/**
 * While `back` is given, Escape in the modal this renders in runs it and leaves the modal open. Its element goes on the
 * ref returned: of several given at once, the one drawn innermost runs, so a step inside a step wins. Outside a modal
 * it does nothing.
 */
export function useModalBack<T extends HTMLElement>(back: (() => void) | undefined): RefObject<T | null> {
    const register = useContext(ModalBackContext);
    const ref = useRef<T>(null);
    useEffect(() => (register && back ? register(ref, back) : undefined), [register, back]);
    return ref;
}

/** The card chrome every stacked modal wears, overriding the kit dialog's own grid/padding/width. */
const PANEL =
    'bg-grey-0 flex max-h-[calc(100dvh-64px)] w-[600px] flex-col gap-0 overflow-hidden rounded-[12px] p-0 max-sm:w-full sm:max-w-none';

/** A container-scoped card fits the container rather than the viewport. */
const SCOPED_PANEL = 'absolute max-h-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:max-w-[calc(100%-2rem)]';

const ModalProvider = ({ children }: Props): JSX.Element => {
    const [modalStack, setModalStack] = useState<ModalOptions[]>([]);

    const contextValue = useMemo(() => ({ setModalStack }), [setModalStack]);

    return (
        <ModalContext.Provider value={contextValue}>
            <ModalCountContext.Provider value={modalStack.length}>{children}</ModalCountContext.Provider>
            {modalStack.map(
                (modalOptions, index) =>
                    modalOptions.content &&
                    (asksForAlertDialog(modalOptions.content) ? (
                        // oxlint-disable-next-line react-doctor/no-array-index-as-key -- modal stack push/pop; ModalOptions has no stable id
                        <StackedAlertDialog key={index} modalOptions={modalOptions} index={index} />
                    ) : (
                        // oxlint-disable-next-line react-doctor/no-array-index-as-key -- as above
                        <StackedModal key={index} modalOptions={modalOptions} index={index} />
                    ))
            )}
        </ModalContext.Provider>
    );
};

// The create dialog sits on the modal layer itself, so a modal opened from it starts one step up.
const stackZIndex = (index: number, fromCreateDialog?: boolean) =>
    `calc(var(--z-modal) + ${fromCreateDialog ? index + 1 : index})`;

function StackedAlertDialog({ modalOptions, index }: { modalOptions: ModalOptions; index: number }) {
    const isNew = useIsNewDesign();
    if (!isNew) {
        return <StackedModal modalOptions={modalOptions} index={index} />;
    }
    const { fromCreateDialog, onCancel } = modalOptions;
    return (
        <AlertDialog
            open
            onOpenChange={(open) => {
                if (!open) {
                    onCancel();
                }
            }}
        >
            <AlertDialogContent style={{ zIndex: stackZIndex(index, fromCreateDialog) }}>
                <OpenedFromCreateDialogProvider value={!!fromCreateDialog}>
                    <InAlertDialogContext.Provider value={true}>{modalOptions.content}</InAlertDialogContext.Provider>
                </OpenedFromCreateDialogProvider>
            </AlertDialogContent>
        </AlertDialog>
    );
}

function StackedModal({ modalOptions, index }: { modalOptions: ModalOptions; index: number }) {
    const { container, modalStyle, fromCreateDialog, classNames } = modalOptions;
    const backs = useRef(new Map<RefObject<HTMLElement | null>, () => void>());
    const register = useCallback<RegisterBack>((drawnIn, back) => {
        backs.current.set(drawnIn, back);
        return () => backs.current.delete(drawnIn);
    }, []);
    const zIndex = stackZIndex(index, fromCreateDialog);
    const overlayZIndex = modalStyle?.zIndex ?? (fromCreateDialog ? zIndex : undefined);
    return (
        <Dialog
            open
            // A scoped modal blocks only its container: focus is kept in it, but
            // the page around the container is not locked or covered.
            modal={container ? 'trap-focus' : true}
            onOpenChange={(open, details) => {
                if (open) {
                    return;
                }
                if (details.reason === 'outside-press' && modalOptions.keepOnOutsidePress) {
                    details.cancel();
                    return;
                }
                const back = details.reason === 'escape-key' ? innermost(backs.current) : undefined;
                if (back) {
                    details.cancel();
                    back();
                } else {
                    modalOptions.onCancel();
                }
            }}
        >
            <DialogContent
                showCloseButton={false}
                container={container}
                overlayProps={{
                    className: cn(container && 'absolute', classNames?.overlay),
                    // The backdrop climbs with a pinned modal, so it still covers,
                    // blurs and blocks the overlay the modal was pinned above.
                    style: overlayZIndex == null ? undefined : { zIndex: overlayZIndex },
                }}
                className={cn(PANEL, container && SCOPED_PANEL, classNames?.panel)}
                style={{
                    // The stack climbs one step per modal, so a modal opened from a modal
                    // paints over it. `calc()` resolves to an integer, which is what z-index takes.
                    // A caller's own `zIndex` wins, for a modal that has to clear a higher
                    // fixed overlay.
                    zIndex,
                    ...modalStyle,
                }}
            >
                <OpenedFromCreateDialogProvider value={!!fromCreateDialog}>
                    <InStackedModalContext.Provider value={true}>
                        <ModalBackContext.Provider value={register}>{modalOptions.content}</ModalBackContext.Provider>
                    </InStackedModalContext.Provider>
                </OpenedFromCreateDialogProvider>
            </DialogContent>
        </Dialog>
    );
}

/** The way back drawn in no other's element: the innermost step's. */
function innermost(backs: Map<RefObject<HTMLElement | null>, () => void>): (() => void) | undefined {
    const drawn = [...backs].filter(([drawnIn]) => drawnIn.current);
    return drawn.find(
        ([drawnIn]) => !drawn.some(([other]) => other !== drawnIn && drawnIn.current?.contains(other.current))
    )?.[1];
}

export default ModalProvider;
